// Utilities for time-based contest results.
//
// IMPORTANT: There are two historical encodings in DB/UI:
// 1) seconds (preferred for comparisons)
// 2) digits mmss/hhmmss (legacy)
//
// For *input* / comparisons we should normalize clock strings into seconds.
// For *display* we keep heuristics to show both formats nicely.

export function parseClockToSeconds(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;

  const s = String(raw).trim();
  if (!s) return null;

  if (!s.includes(':')) {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  const parts = s.split(':').map((p) => p.trim());
  if (parts.some((p) => p === '' || !/^[0-9]+(?:\.[0-9]+)?$/.test(p))) return null;

  if (parts.length === 2) {
    const mm = Number(parts[0]);
    const ss = Number(parts[1]);
    if (!Number.isFinite(mm) || !Number.isFinite(ss)) return null;
    return mm * 60 + ss;
  }

  if (parts.length === 3) {
    const hh = Number(parts[0]);
    const mm = Number(parts[1]);
    const ss = Number(parts[2]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm) || !Number.isFinite(ss)) return null;
    return hh * 3600 + mm * 60 + ss;
  }

  return null;
}

// Some contests store time as digits: mmss or hhmmss (e.g. 930 => 9:30, 01230 => 12:30, 102030 => 10:20:30)
export function formatTimeDigitsToClock(timeDigits: number): string {
  if (!Number.isFinite(timeDigits)) return String(timeDigits);
  const raw = Math.max(0, Math.round(timeDigits));
  const s = String(raw);
  if (s.length <= 2) {
    return `0:${s.padStart(2, '0')}`;
  }
  if (s.length <= 4) {
    const mm = Number(s.slice(0, -2) || '0');
    const ss = Number(s.slice(-2));
    return `${mm}:${String(ss).padStart(2, '0')}`;
  }
  const padded = s.padStart(6, '0');
  const hh = Number(padded.slice(0, -4) || '0');
  const mm = Number(padded.slice(-4, -2));
  const ss = Number(padded.slice(-2));
  return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

// Time results in DB are historically inconsistent:
// - some contests store time as digits (mmss/hhmmss)
// - some contests store plain seconds
// This helper displays both sensibly.
export function formatTimeValueToClock(val: number): string {
  if (!Number.isFinite(val)) return String(val);
  const raw = Math.max(0, Math.round(val));

  // Heuristic: if it looks like "time digits" (>= 100), treat as digits.
  // Otherwise, treat as seconds.
  if (raw >= 100) {
    return formatTimeDigitsToClock(raw);
  }

  const mm = Math.floor(raw / 60);
  const ss = raw % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

export function formatSecondsToHHMMSS(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds)) return String(totalSeconds);
  const raw = Math.max(0, Math.round(totalSeconds));
  const hh = Math.floor(raw / 3600);
  const mm = Math.floor((raw % 3600) / 60);
  const ss = raw % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}
